import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useMilestonesTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { formatDateLabel } from '../../../../strategy.helpers';
import { Metric } from '../../../../strategy.type';
import { type ChartData, type ChartDataItem, DataKey } from './milestone-progress-chart.type';

/**
 * Hook to generate chart data for milestone progress visualization
 *
 * @param {Metric} metric - The metric type to display in the chart (e.g., Tasks, Story Points)
 * @returns An array of chart data items containing date, completed, and total values
 */
const useChartData = (metric: Metric): ChartData => {
  const selectedMilestone = useMilestonesTabStore((state) => state.selectedMilestone);

  const {
    completed_subtasks_over_time: completedDataset = { [metric]: {} },
    total_subtasks_over_time: totalDataset = { [metric]: {} },
    activity_end_date,
    target_end_date,
  } = selectedMilestone || {};

  const chartData = useMemo(() => {
    if (!selectedMilestone) {
      return [];
    }

    const existingDates = Object.keys(totalDataset[metric] || {}).sort();
    const currentDate = dayjs();
    let allDates = [...existingDates];

    // If activity_end_date is provided and is after the current data, extend the date range
    if (activity_end_date) {
      const activityEndDate = dayjs(activity_end_date, 'YYYY-MM-DD');
      const lastExistingDate = dayjs(existingDates[existingDates.length - 1], 'YYYY-MM');

      // Generate monthly dates from the last existing date to the activity_end_date
      if (activityEndDate.isAfter(lastExistingDate, 'month')) {
        let currentMonth = lastExistingDate.clone();
        const extendedDates = [existingDates[existingDates.length - 1]];

        while (currentMonth.isBefore(activityEndDate, 'month')) {
          currentMonth = currentMonth.add(1, 'month');
          const monthString = currentMonth.format('YYYY-MM');
          if (!existingDates.includes(monthString) && !extendedDates.includes(monthString)) {
            extendedDates.push(monthString);
          }
        }

        // Include the month containing activity_end_date
        const activityEndMonthString = activityEndDate.format('YYYY-MM');
        if (!existingDates.includes(activityEndMonthString) && !extendedDates.includes(activityEndMonthString)) {
          extendedDates.push(activityEndMonthString);
        }

        allDates = [...existingDates, ...extendedDates.slice(1)].sort();
      }
    }

    return allDates.map((date) => {
      const dateObj = dayjs(date, 'YYYY-MM');
      const isCurrentMonth = dateObj.isSame(currentDate, 'month');
      const isFuture = dateObj.isAfter(currentDate, 'month');
      const total = ((totalDataset[metric] || {}) as Record<string, number | null>)[date] || 0;
      const completed = ((completedDataset[metric] || {}) as Record<string, number | null>)[date] || 0;

      // Format target end date to match chart date format (YYYY-MM)
      const formattedTargetEndDate = target_end_date ? dayjs(target_end_date).format('YYYY-MM') : undefined;

      if (isFuture) {
        return {
          date: formatDateLabel(date),
          rawDate: date,
          [DataKey.TotalRegular]: null,
          [DataKey.CompletedRegular]: null,
          [DataKey.TotalFuture]: null,
          [DataKey.CompletedFuture]: null,
          isCurrentMonth,
          targetEndDate: formattedTargetEndDate,
        } as ChartDataItem;
      } else {
        return {
          date: formatDateLabel(date),
          rawDate: date,
          [DataKey.TotalRegular]: total,
          [DataKey.CompletedRegular]: completed,
          [DataKey.TotalFuture]: isCurrentMonth ? total : null,
          [DataKey.CompletedFuture]: isCurrentMonth ? completed : null,
          isCurrentMonth,
          targetEndDate: formattedTargetEndDate,
        } as ChartDataItem;
      }
    });
  }, [completedDataset, totalDataset, metric, selectedMilestone, activity_end_date, target_end_date]);

  return chartData;
};

export { useChartData };
